import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DatePicker from 'react-date-picker';
import dayjs from 'dayjs';
import { CalendarRange } from 'lucide-react';

import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';

function normalizeDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function DateFilter({
  value,
  onChange,
  name,
  onCalendarClose: externalOnCalendarClose,
  ...props
}) {
  const pickerValue = useMemo(() => normalizeDate(value), [value]);
  const [internalValue, setInternalValue] = useState(pickerValue);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setInternalValue(pickerValue);
  }, [pickerValue]);

  const handleChange = useCallback(
    (nextValue) => {
      const normalizedValue = normalizeDate(nextValue);
      const formattedValue = normalizedValue
        ? dayjs(normalizedValue).format('YYYY-MM-DD')
        : '';

      setInternalValue(normalizedValue);

      if (onChange) {
        onChange({
          target: {
            name,
            value: formattedValue,
          },
          currentTarget: {
            name,
            value: formattedValue,
          },
          value: formattedValue,
        });
      }
    },
    [name, onChange],
  );

  // Mirror react-select portal behavior: use absolute positioning in body portal
  // and keep coordinates updated while open.
  const applyCalendarPosition = useCallback(() => {
    const calendar = document.querySelector(
      '.react-date-picker__calendar--open',
    );
    if (!calendar || !wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const top = rect.bottom + window.pageYOffset + 4;
    const left = rect.left + window.pageXOffset;

    calendar.style.setProperty('position', 'absolute');
    calendar.style.setProperty('top', `${top}px`);
    calendar.style.setProperty('left', `${left}px`);
    calendar.style.setProperty('right', 'auto');
    calendar.style.setProperty('bottom', 'auto');
    calendar.style.setProperty('z-index', '99999');
  }, []);

  useEffect(() => {
    if (!isCalendarOpen) return;

    const update = () => {
      requestAnimationFrame(applyCalendarPosition);
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [applyCalendarPosition, isCalendarOpen]);

  const handleCalendarOpen = useCallback(() => {
    setIsCalendarOpen(true);
    requestAnimationFrame(applyCalendarPosition);
  }, [applyCalendarPosition]);

  const handleCalendarClose = useCallback(() => {
    setIsCalendarOpen(false);
    if (externalOnCalendarClose) {
      externalOnCalendarClose();
    }
  }, [externalOnCalendarClose]);

  return (
    <div ref={wrapperRef}>
      <DatePicker
        {...props}
        value={internalValue}
        onChange={handleChange}
        onCalendarOpen={handleCalendarOpen}
        onCalendarClose={handleCalendarClose}
        maxDate={new Date()}
        format="dd-MM-y"
        clearIcon={null}
        calendarIcon={<CalendarRange size={18} color="white" />}
        portalContainer={document.body}
      />
    </div>
  );
}

export { DateFilter };
